import clsx from "clsx";
import React, { useMemo } from "react";
import { Button, DownloadIcon } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import PositionsDetailTable from "~/pages/LicenseManagement/parts/PositionsDetailTable";
import Modal from "~/shared/ui/Fields/Modal";
import {
  ILicenseRequestRow,
  useGetLicenseRequestById,
  useSetLicenseRequest,
} from "~/entity/Licenses";
import { LicenseRequestsService } from "~/entity/Licenses/api/licenseRequestsService";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { ILicenseRequestDetailPosition } from "~/pages/LicenseManagement/model/types";
import { usePermissions } from "~/shared/context/PermissionContext";

import s from "~/pages/LicenseManagement/LicenseManagement.less";

interface LicenseDecisionResultModalProps {
  requests: ILicenseRequestRow[];
}

const LicenseDecisionResultModal = ({
  requests,
}: LicenseDecisionResultModalProps) => {
  const { t } = useTranslation();
  const { openModal, currentId } = useURLParamsGetter<{
    openModal: string;
    currentId: string;
  }>();
  const setParams = useURLParamsSetter();
  const isOpen = openModal === "DecisionResult";
  const { hasPermission } = usePermissions();
  const canExport = hasPermission(
    "employeeAccounting.license-requests.id.export.get",
  );
  const canDecide = hasPermission("employeeAccounting.license-requests.id.put");
  const { approveLicenseRequest } = useSetLicenseRequest();

  const currentRequest = useMemo(
    () => requests.find((item) => item.id === currentId) || null,
    [requests, currentId],
  );

  const { request } = useGetLicenseRequestById(
    isOpen && currentId ? currentId : undefined,
  );

  const positions = useMemo<ILicenseRequestDetailPosition[]>(
    () =>
      (request?.items || []).map((item, index) => ({
        positionId: item.instanceId ?? index,
        positionName: item.positions?.length
          ? item.positions.map((p) => `${p.code}. ${p.name}`).join(", ")
          : item.positionName || "-",
        quantity: item.quantity,
        unitPrice:
          item.priceWithDiscount != null
            ? Number(item.priceWithDiscount)
            : item.unitPrice == null
              ? undefined
              : Number(item.unitPrice),
        discountPercent: item.discountPercent ?? null,
        discountAmount: item.discountAmount ?? null,
        priceWithDiscount:
          item.priceWithDiscount == null
            ? null
            : Number(item.priceWithDiscount),
        lineTotal: item.cost == null ? null : Number(item.cost),
      })),
    [request],
  );

  const totalCost = Number(request?.totalCost || 0);

  const hasDecision = Boolean(currentRequest?.decision);
  const isRejected = currentRequest?.decision === "rejected";
  const decisionLabel = isRejected
    ? t("licenseManagement.decisions.rejected")
    : t("licenseManagement.decisions.accepted");

  const closeModal = () =>
    setParams({ openModal: undefined, currentId: undefined });

  const handleExportExcel = () => {
    if (!currentId) {
      return;
    }

    document.location.href =
      LicenseRequestsService.GET.exportByIdUrl(currentId);
  };

  const canConfirmCalculation = Boolean(
    canDecide &&
    currentRequest?.needsDecision &&
    currentRequest.actionType === "baseIssue",
  );

  const handleConfirmCalculation = () => {
    if (!currentId) {
      return;
    }

    approveLicenseRequest.mutate({ id: currentId, data: {} });
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      title={t("licenseManagement.resultModal.title")}
      actionClose={closeModal}
      w={948}
    >
      <div className={s.modalForm}>
        <div>
          <div className={s.infoBanner}>
            <span>
              <strong>
                {t("licenseManagement.resultModal.organization")}:
              </strong>{" "}
              {currentRequest?.dealerName ||
                currentRequest?.organization ||
                "-"}
            </span>
          </div>
          {hasDecision && (
            <div
              className={clsx(
                s.decisionBanner,
                isRejected
                  ? s.decisionBannerRejected
                  : s.decisionBannerAccepted,
              )}
            >
              <span>
                <strong>{t("licenseManagement.resultModal.decision")}:</strong>{" "}
                {decisionLabel}
                {isRejected ? "." : ""}
              </span>
              {currentRequest?.comment && (
                <span>
                  <strong>{t("licenseManagement.resultModal.comment")}:</strong>{" "}
                  {currentRequest.comment}
                </span>
              )}
            </div>
          )}
        </div>

        {canExport && (
          <div className={s.calcToolbar}>
            <Button
              color="secondary"
              variant="outlined"
              fontSize="10"
              size="small"
              radius={30}
              icon={<DownloadIcon size={12} />}
              onClick={handleExportExcel}
            >
              {t("licenseManagement.downloadExcel")}
            </Button>
          </div>
        )}

        {request && (
          <PositionsDetailTable positions={positions} totalCost={totalCost} />
        )}

        <div className={s.modalButtons}>
          <Button
            bold
            color="secondary"
            fontSize="14"
            size="medium"
            radius={5}
            onClick={closeModal}
          >
            {t("licenseManagement.resultModal.close")}
          </Button>
          {canConfirmCalculation && (
            <Button
              bold
              color="primary"
              fontSize="14"
              size="medium"
              radius={5}
              loading={approveLicenseRequest.isLoading}
              onClick={handleConfirmCalculation}
            >
              {t("licenseManagement.resultModal.confirm")}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LicenseDecisionResultModal;
